import React from 'react';

import { slugify } from '@/components/atoms/layout/utils';
import { cn } from '@/lib/utils';

import { StrapiThemes } from '@/components/themeProviderUtils';
import { ButtonProps } from '@/decodo/components/atoms/Button';
import Heading from '@/decodo/components/atoms/Heading';
import { LinkButtonProps } from '@/decodo/components/atoms/LinkButton';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';

export type BlockHeader = Pick<
  BlockHeaderContentProps,
  'title' | 'subtitle' | 'buttonPosition' | 'buttons'
> & {
  theme?: StrapiThemes;
};

export function generateCMSButtonsForBlockHeader(
  buttons: LinkButtonStrapiProps[]
): ((props: object) => React.ReactNode)[] {
  return buttons.map((button) => {
    return (props: object) => (
      <LinkButtonStrapi
        key={button.text + button.url}
        block={{
          ...button,
          variant: button.variant ?? 'outlined',
          color: button.color ?? 'secondary',
        }}
        inner={props}
      />
    );
  });
}

interface BlockHeaderContentProps {
  title?: React.ReactNode;
  titleLevel?: 'h1' | 'h2';
  subtitle?: React.ReactNode | RichTextFieldStrapiProps;
  renderButtons?: ((props: object) => React.ReactNode)[];
  buttonPosition?: 'top' | 'bottom';
  alignment?: 'left' | 'center';
  classNames?: {
    root?: string;
    subtitle?: string;
    children?: string;
    title?: string;
    header?: string;
    button?: string;
  };
  children?: React.ReactNode;
  bottomChildren?: React.ReactNode;
  topChildren?: React.ReactNode;
  // strapi specific
  buttons?: LinkButtonStrapiProps[];
}

export const blockHeaderTopSpacingClasses = 'mb-8 md:mb-[3.75rem] lg:mb-20';
export const blockHeaderBottomSpacingClasses = 'mt-8 md:mt-[3.75rem] lg:mt-20';
export const blockContentBaseClasses = 'flex flex-wrap w-full gap-6';
export const blockContentAlignClasses = {
  center:
    'md:text-center md:justify-center md:items-center text-left items-center justify-start',
  left: 'text-left md:justify-start md:items-start items-center justify-start',
};

export function handleRenderButtons(
  renderButtons: BlockHeaderContentProps['renderButtons']
) {
  if (!renderButtons) return null;

  return renderButtons.map((renderButton) =>
    renderButton({
      classNames: { root: 'w-full md:w-auto' },
    } satisfies Partial<ButtonProps | LinkButtonProps>)
  );
}
const defaultButtonPosition: BlockHeaderContentProps['buttonPosition'] =
  'bottom';

export function BlockHeaderContent({
  title,
  titleLevel = 'h2',
  subtitle,
  renderButtons,
  buttons,
  buttonPosition = defaultButtonPosition,
  alignment,
  classNames,
  children,
  bottomChildren,
  topChildren,
}: BlockHeaderContentProps) {
  const buttonRenderPosition = buttonPosition || defaultButtonPosition;

  let extendedRenderButton = generateCMSButtonsForBlockHeader(buttons ?? []);
  if (renderButtons && renderButtons.length > 0) {
    extendedRenderButton = [...renderButtons, ...extendedRenderButton];
  }

  const selectedAlignmentClasses =
    blockContentAlignClasses[alignment ?? 'center'];

  const slugifiedName = slugify(typeof title === 'string' ? title : '');

  return (
    <div className={cn('flex w-full flex-col', classNames?.root)}>
      {(title ||
        subtitle ||
        topChildren ||
        (!!extendedRenderButton.length && buttonRenderPosition === 'top')) && (
        <div
          id={slugifiedName}
          className={cn(
            'flex w-full flex-col',
            blockHeaderTopSpacingClasses,
            selectedAlignmentClasses,
            'blockHeaderTitle',
            classNames?.header
          )}
        >
          {title && (
            <div className={cn('w-full', classNames?.title)}>
              {React.isValidElement(title) ? (
                title
              ) : (
                <Heading level={titleLevel ?? 'h2'}>{title}</Heading>
              )}
            </div>
          )}
          {subtitle && (
            <div
              className={cn(
                'mt-5 w-full text-gray-500 lg:mt-6',
                classNames?.subtitle
              )}
            >
              {React.isValidElement(subtitle) ? (
                subtitle
              ) : (
                <RichTextFieldStrapi
                  block={subtitle as RichTextFieldStrapiProps}
                />
              )}
            </div>
          )}
          {buttonRenderPosition === 'top' &&
            extendedRenderButton.length > 0 && (
              <div
                className={cn(
                  'mt-8 flex w-full flex-col gap-6 md:mt-9 md:flex-row',
                  selectedAlignmentClasses,
                  classNames?.button
                )}
                data-tracker-component='action'
              >
                {handleRenderButtons(extendedRenderButton)}
              </div>
            )}
          {topChildren && (
            <div
              className={cn(
                'mt-8 flex w-full md:mt-9',
                selectedAlignmentClasses
              )}
            >
              {topChildren}
            </div>
          )}
        </div>
      )}
      {children && (
        <div
          id='block-header-content'
          className={cn(
            blockContentBaseClasses,
            selectedAlignmentClasses,
            'blockHeaderContent',
            classNames?.children
          )}
        >
          {children}
        </div>
      )}
      {buttonRenderPosition === 'bottom' && extendedRenderButton.length > 0 && (
        <div
          className={cn(
            'flex w-full flex-col gap-6 md:flex-row',
            selectedAlignmentClasses,
            blockHeaderBottomSpacingClasses,
            classNames?.button
          )}
          data-tracker-component='action'
        >
          {handleRenderButtons(extendedRenderButton)}
        </div>
      )}
      {bottomChildren && (
        <div className={cn(selectedAlignmentClasses)}>{bottomChildren}</div>
      )}
    </div>
  );
}
